




import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { ApiService } from 'src/app/services/api/api.service';

import { ErrorModalComponent } from 'src/app/shared/modals/error-modal/error-modal.component';
import { SuccessModalComponent } from 'src/app/shared/modals/success-modal/success-modal.component';
import { Router } from '@angular/router';
import {
  handleFileUploaded,
  handleFileUploadError,
} from 'src/app/shared/components/upload-input/file-upload-helper';
import { ConfirmActionComponent } from 'src/app/shared/components/confirm-action/confirm-action.component';
import { AuthService } from 'src/app/services/auth/auth.service';
import { UtilitiesService } from 'src/app/services/utilities/utilities.service';

@Component({
  selector: 'app-street-naming-details-modal',
  standalone: false,
  templateUrl: './street-naming-details-modal.component.html',
  styleUrl: './street-naming-details-modal.component.scss'
})
export class StreetNamingDetailsModalComponent {
  isSubmitting: boolean;
  isVerifyingAsin: boolean;
  constructor(
    private formBuilder: FormBuilder,
    private apiService: ApiService,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<StreetNamingDetailsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private utilitiesService: UtilitiesService,
    private router: Router,
    private authService: AuthService
  ) { }

  showErrorMessages: boolean = false;

  form: FormGroup;

  wardList: any;

  streetTypes: any[];


  ngOnInit(): void {


    this.fetchWards()
    this.initializeForm();
    this.fetchDetails();
    this.fetchStreetTypes();

  }

  fetchWards() {
    const lgaId = this.authService.getUser()?.lgaId;

    if (lgaId) {
      this.apiService.get('/utility/all/ward/' + lgaId).subscribe(
        (response) => {
          this.wardList = response;
        },
        (error) => {
          console.error('Error Fetching Local Government: ', error);
        }
      );

    }
  }

  fetchDetails() {
    this.form.disable()

    if (this.dialogData.entity.id) {
      this.apiService.get('/street-name/details/' + this.dialogData?.entity?.id, {}).subscribe((res) => {

        if (res.success) {
          this.mapToFormValues(res.data);
        }
      })
    }
  }

  fetchStreetTypes() {
    const lgaId = this.authService.getUser()?.lgaId;

    if (lgaId) {
      this.apiService.get('/street-name/types').subscribe(
        (response) => {
          this.streetTypes = response.data;
        },
        (error) => {
          console.error('Error Fetching Local Government: ', error);
        }
      );

    }

  }


  mapToFormValues(data: any): any {
    const values = {
      compoundName: data.compoundName,
      ward: data?.wardId || null,
      documentUpload: { file: null, readableUrl: data.landOwnershipCert },
      lastName: data?.lastName || '',
      firstName: data?.firstName || '',
      age: data?.age || null,
      middleName: data?.middleName || '',
      phoneNumber: data?.phoneNumber || '',
      email: data?.email || '',
      street: data?.street || '',
      address: data?.address || '',
      streetType: data?.streetType || '',
      streetName: data?.streetName || ''
    };

    this.form.patchValue(values);

  }


  initializeForm() {
    this.form = this.formBuilder.group({
      compoundName: ['', [Validators.required]],
      ward: [null, [Validators.required]],
      documentUpload: [null],
      lastName: ['', [Validators.required, Validators.maxLength(60)]],
      firstName: ['', [Validators.required, Validators.maxLength(60)]],
      address: ['', [Validators.required, Validators.maxLength(60)]],
      age: [null, [Validators.required]],
      middleName: ['', [Validators.maxLength(60)]],
      phoneNumber: [
        '',
        [Validators.required, this.phoneNumberValidator() as ValidatorFn],
      ],
      email: ['', [Validators.maxLength(60), this.emailValidator()]],
      streetName: ['', [Validators.required, Validators.maxLength(120)]],
      streetType: ['', [Validators.required, Validators.maxLength(120)]],
    });
  }


  phoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value === null || control.value === undefined) {
        return null;
      }

      const cleanedPhoneNumber = control.value
        .replace(/\s/g, '')
        .replace(/-/g, '');

      const isDigitsOnly = /^\d+$/.test(cleanedPhoneNumber);

      if (
        cleanedPhoneNumber.length === 0 ||
        (cleanedPhoneNumber.length === 11 && isDigitsOnly) ||
        (cleanedPhoneNumber.length === 13 &&
          cleanedPhoneNumber.startsWith('234')) ||
        (cleanedPhoneNumber.length === 14 &&
          cleanedPhoneNumber.startsWith('+234'))
      ) {
        return null;
      } else {
        return { invalidPhoneNumber: true };
      }
    };
  }

  emailValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

      if (control.value && !emailPattern.test(control.value)) {
        return { invalidEmail: true };
      }

      return null;
    };
  }

  onFileChange(
    fileDetails: { file: File; readableUrl: string } | null,
    controlName: string
  ) {
    handleFileUploaded(fileDetails, this.form, controlName);
  }

  onError(errorResponse: any, controlName: string) {
    handleFileUploadError(errorResponse, this.form, controlName);
  }

  verifyAsin() {
    const asin = this.form.get('asin')?.value;

    this.isVerifyingAsin = true;

    this.apiService
      .get('/utility/asin/validate?tin=' + asin)
      .pipe(
        finalize(() => {
          this.isVerifyingAsin = false;
        })
      )
      .subscribe(
        (response: any) => {
          this.form.patchValue({
            businessName: response?.firstName,
          });

          this.openAsinVerificationSuccessModal('ASIN verified successfuly');
        },
        (error) => {
          this.openErrorModal(error?.message);
        }
      );
  }


  hasAccess(permissions: string[]) {
    return this.authService.hasAnyPermission(permissions)
  }


  openSuccessModal(message: string) {
    let dialogRef = this.dialog.open(SuccessModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { successMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.initializeForm();
      this.dialogRef.close({reload: true})
    });

    return dialogRef
  }

  openErrorModal(message: string) {
    let dialogRef = this.dialog.open(ErrorModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { errorMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => { });
  }

  openConfirmModal() {
    this.dialog.open(ConfirmActionComponent, {
      width: '400px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'operation-modal', 'modal-no-overflow-changed'],
      disableClose: true,

      data: {
        title: 'Create Super Agent?',
        body: '',
        confirmText: 'Yes',
        lottieIconPath: 'assets/lottie/alert.json',
        onConfirm: () => {
          this.create();
        },
        onCancel: () => { },
        showCancelButton: true,
        showConfirmButton: true,
      },
    });
  }

  async create(): Promise<void> {
    if (this.form.valid) {
      this.isSubmitting = true;


      try {
        const formData = this.form.value;
        const request = await this.mapFormValuesToObject(formData);

        const url = '/lga-identification/apply';

        this.apiService
          .post(url, request)
          .pipe(finalize(() => (this.isSubmitting = false)))
          .subscribe(
            (res) => {
              if (res.success) {
                this.openSuccessModal(res.message || 'LGA identification application successful')
              }
              else {
                this.openErrorModal(res.error || res.message);

              }
            },
            (error) => {
              this.openErrorModal(error?.message);
            }
          );
      } catch (error) {
        this.isSubmitting = false;
        this.openErrorModal('Failed to process form data');
      }
    }
  }



  isQueryingApplication: boolean;
  isApprovingApplication: boolean;

  queryApplication() {
    const formValues = this.form.value;

    this.openConfirmationModal({
      title: 'Are you sure you want to decline this application?',
      onConfirm: () => {

        this.isQueryingApplication = true;

        this.apiService.post('/workflow/get-next-approving-officer', {
          entityId: this.dialogData?.entity?.id,
          workflowName: 'STREET_NAMING',
          type: 'QUERIED'
        }).pipe(
          finalize(() => {
            this.isQueryingApplication = false;
          })
        ).subscribe((res) => {
          if (res.success) {
            this.openSuccessModal(res.message || 'Application queried successfully')
          }
          else {
            this.openErrorModal(res?.error || res?.message);

          }
        },
          (error) => {
            this.openErrorModal(error || 'Unable to decline application');
          })
      },
      onCancel: () => console.log('Decline action cancelled'),
    });
  }

  openConfirmationModal({
    title,
    onConfirm,
    onCancel = () => { },
  }: {
    title: string;
    onConfirm: () => void;
    onCancel?: () => void;
  }) {
    this.dialog.open(ConfirmActionComponent, {
      width: '400px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      disableClose: true,
      data: {
        title,
        body: '',
        confirmText: 'Yes',
        lottieIconPath: 'assets/lottie/alert.json',
        onConfirm,
        onCancel,
        showCancelButton: true,
        showConfirmButton: true,
      },
    });
  }

  approveApplication() {
    this.openConfirmationModal({
      title: 'Are you sure you want to approve this application?',
      onConfirm: () => {
        this.isApprovingApplication = true;



        this.apiService.post('/workflow/get-next-approving-officer', {
          entityId: this.dialogData?.entity?.id,
          workflowName: 'STREET_NAMING',
          type: 'APPROVED'
        }).pipe(
          finalize(() => {
            this.isApprovingApplication = false;
          })
        ).subscribe((res) => {
          if (res.success) {
            this.openSuccessModal(res.message)
          }
          else {
            this.openErrorModal(res?.error || res?.message);

          }
        },
          (error) => {
            this.openErrorModal(error || 'Unable to approve application');
          })
      },
      onCancel: () => console.log('Approval action cancelled'),
    });
  }

  async mapFormValuesToObject(formValue: any): Promise<any> {
    let approvalLetterBase64 = '';

    if (formValue.communityApproval?.file) {
      const fullBase64 = await this.convertFileToBase64(
        formValue.communityApproval.file
      );
      approvalLetterBase64 = fullBase64.split(',')[1];
    }

    return {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      middleName: formValue.middleName,
      phoneNumber: formValue.phoneNumber,
      email: formValue.email,
      age: formValue.age,
      street: formValue.street,
      compoundName: formValue.compoundName,
      ward: formValue.ward,
      communityApproval: approvalLetterBase64, // Ensure it matches DTO
    };
  }

  openAsinVerificationSuccessModal(message: string) {
    let dialogRef = this.dialog.open(SuccessModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { successMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => { });
  }

  convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }

  back() {
    this.dialogRef.close()
  }
}
